import { useMemo } from 'react'
import { Image, StyleSheet, Text } from 'react-native'

import { Button } from '@zet/design-system/button'
import { Stack, XStack } from '@zet/design-system/layout'
import { useBrandDetailsById } from '@zet/feature-brands/api/brands/hooks'
import { LinearGradient } from 'expo-linear-gradient'

import { primaryButtonStyle } from '../../screens/userHome'
import { getBenefitIcon, getBenefitTexts, getBrandGradient } from './constants'
import { UserFlowProduct } from './types'
import useUserFlowApplyLink from './useUserFlowApplyLink'

const gradientStart = { x: 0, y: 0 }
const gradientEnd = { x: 1, y: 1 }

const UserFlowRecommendationCard = ({
  product,
  customerId,
  onApplySuccess,
  compact = false,
  index,
}: {
  product: UserFlowProduct
  customerId: number
  onApplySuccess?: () => void
  compact?: boolean
  index: number
}) => {
  const { data: brandDetails } = useBrandDetailsById(Number(product?.partnerId))
  const { openGeneratedLink, isLoading } = useUserFlowApplyLink(onApplySuccess)
  const benefitTexts = useMemo(() => getBenefitTexts(product), [product])
  const displayName = (
    product?.name ||
    brandDetails?.name ||
    'Recommended offer'
  ).trim()

  const handleApplyPress = async () => {
    await openGeneratedLink({
      partnerId: Number(product?.partnerId),
      customerId,
      variantId: product?.id,
    })
  }

  return (
    <LinearGradient
      colors={[getBrandGradient(index)?.[0], '#FFFDF7', '#FFFFFF', '#FFFFFF']}
      locations={[0, 0.24, 0.5, 1]}
      start={gradientStart}
      end={gradientEnd}
      style={styles.gradient}
    >
      <Stack
        style={[styles.card, compact ? styles.cardCompact : styles.cardFull]}
      >
        <Stack style={styles.cardBody}>
          <XStack style={styles.header}>
            <Stack style={styles.titleContainer}>
              <Text style={styles.title}>{displayName}</Text>
            </Stack>
            <XStack style={styles.logoContainer}>
              {product?.imageUrl ? (
                <Image
                  source={{ uri: product?.imageUrl }}
                  style={styles.logo}
                  resizeMode="contain"
                />
              ) : null}
            </XStack>
          </XStack>

          {product?.leadExists ? (
            <Stack style={styles.leadContainer}>
              <Text style={styles.leadTitle}>Application started</Text>
              <Text style={styles.leadDescription}>
                Pick up this loan journey from where the customer left off.
              </Text>
            </Stack>
          ) : (
            <Stack style={styles.benefitsContainer}>
              {benefitTexts?.map((text, benefitIndex) => (
                <XStack
                  key={`${text}-${benefitIndex}`}
                  style={styles.benefitRow}
                >
                  <Image
                    source={{ uri: getBenefitIcon(benefitIndex) }}
                    style={styles.benefitIcon}
                    resizeMode="contain"
                  />
                  <Text style={styles.benefitText}>
                    {text?.replace(/^.*?:\s*/, '')}
                  </Text>
                </XStack>
              ))}
            </Stack>
          )}
        </Stack>

        <Stack style={styles.buttonContainer}>
          <Button
            size="medium"
            loading={isLoading}
            onPress={handleApplyPress}
            style={primaryButtonStyle}
            textStyle=""
          >
            <Text style={styles.buttonText}>
              {product?.leadExists
                ? 'Continue Application'
                : 'Start Application'}
            </Text>
          </Button>
        </Stack>
      </Stack>
    </LinearGradient>
  )
}

const styles = StyleSheet.create({
  benefitRow: {
    alignItems: 'center',
  },
  benefitIcon: {
    width: 20,
    height: 20,
  },
  benefitText: {
    marginLeft: 8,
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 13,
    lineHeight: 19,
    letterSpacing: -0.26,
    color: '#374151',
  },
  benefitsContainer: {
    marginTop: 16,
    gap: 12,
    minHeight: 84,
  },
  button: {
    backgroundColor: '#832DC2',
    borderColor: '#7929B4',
    borderWidth: 1.2,
    borderRadius: 12,
    marginTop: 16,
  },
  buttonText: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 14,
    lineHeight: 24,
    letterSpacing: -0.28,
    color: '#FFFFFF',
  },
  card: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
  },
  cardBody: {
    flex: 1,
  },
  cardCompact: {
    width: 289,
    minHeight: 260,
  },
  cardFull: {
    width: '100%',
  },
  buttonContainer: {
    marginTop: 'auto',
  },
  gradient: {
    borderRadius: 24,
  },
  header: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  leadContainer: {
    marginTop: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FFEDD5',
    backgroundColor: '#FFF7ED',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  leadDescription: {
    marginTop: 4,
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: -0.24,
    color: '#4B5563',
  },
  leadTitle: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 13,
    lineHeight: 19,
    letterSpacing: -0.26,
    color: '#C2410C',
  },
  logo: {
    height: 32,
    width: 104,
  },
  logoContainer: {
    alignItems: 'center',
  },
  title: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.32,
    color: '#0C0E11',
  },
  titleContainer: {
    flex: 1,
    paddingRight: 12,
  },
})

export default UserFlowRecommendationCard
